import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import Terms from '@/pages/Signup/Terms';
import type { SignupMemberType } from '../signupTerms';
import { TermsId } from '@/pages/Signup/termsContent';
import ConfirmModal from '@/components/common/elements/ConfirmModal';

import { designSystem } from '@/styles/theme';

interface TermsAgreementProps {
    memberType: SignupMemberType;
    /** 약관 동의 완료 후 다음 스텝(정보 입력)으로 진행 */
    onNext: () => void;
    // 약관별 동의 상태(7개) 부모로 전달 — 가입 폼에서 API payload에 함께 실어 보낸다.
    onAgreementsChange?: (agreements: Record<TermsId, boolean>) => void;
}

const FormContainer = styled.div`
    width: 100%;
`;

const TermsBox = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        padding: 16px;
        margin-bottom: 20px;
    }
`;

const NextButton = styled.button<{ disabled?: boolean }>`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    background: ${props => props.disabled ? '#e5e7eb' : designSystem.colors.primary};
    color: ${props => props.disabled ? '#9ca3af' : '#fff'};
    transition: all 0.3s;

    &:hover:not(:disabled) {
        background: ${designSystem.colors.primaryDark};
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(90, 155, 150, 0.3);
    }

    @media (max-width: 768px) {
        height: 48px;
        font-size: 15px;
    }
`;

const TermsAgreement = ({ onNext, onAgreementsChange, memberType }: TermsAgreementProps) => {
    const [isAllAgreed, setIsAllAgreed] = useState(false);
    const [pushAdvertisingAgreed, setPushAdvertisingAgreed] = useState(false);
    const [showPushNotice, setShowPushNotice] = useState(false);

    const handleAgreementsChange = useCallback((agreements: Record<TermsId, boolean>) => {
        setPushAdvertisingAgreed(agreements.pushAdvertising);
        onAgreementsChange?.(agreements);
    }, [onAgreementsChange]);

    const handleNext = () => {
        if (!isAllAgreed) return;
        if (!pushAdvertisingAgreed) {
            setShowPushNotice(true);
            return;
        }
        onNext();
    };

    return (
        <FormContainer>
            <TermsBox>
                <Terms key={memberType} memberType={memberType} onAllAgreed={setIsAllAgreed} onAgreementsChange={handleAgreementsChange} />
            </TermsBox>

            <NextButton
                type="button"
                disabled={!isAllAgreed}
                onClick={handleNext}
                title={!isAllAgreed ? '필수 약관에 모두 동의해주세요' : undefined}
            >
                다음
            </NextButton>
            <ConfirmModal
                open={showPushNotice}
                title="이벤트·혜택 소식을 놓칠 수 있어요"
                description="이벤트·혜택 알림에 동의하지 않으면 관련 앱 푸시를 받을 수 없어요. 알림을 받지 않고 계속할까요? 동의하지 않아도 가입할 수 있고, 나중에 알림 설정에서 변경할 수 있어요."
                cancelText="동의 항목 확인"
                confirmText="알림 없이 계속"
                onClose={() => setShowPushNotice(false)}
                onConfirm={onNext}
            />
        </FormContainer>
    );
};

export default TermsAgreement;
